/**
 * Comparison - What RigCraft is and is not
 *
 * Sets correct expectations:
 * - What it is NOT: preset files, auto sync, AI replacing decisions
 * - What it IS: faster way to better presets with musical intent
 */

"use client";

import { useTranslations } from "next-intl";
import { ScrollReveal } from "./effects";
import GradientText from "@/components/ui/GradientText";
import { X, Check } from "lucide-react";

export default function Comparison() {
  const t = useTranslations("landing.comparison");

  const isNotItems = ["presetFiles", "autoSync", "aiReplacing"];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-transparent via-pink-900/10 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* What It Is Not */}
          <ScrollReveal delay={0}>
            <div
              className="
                relative overflow-hidden rounded-2xl p-8 h-full
                bg-white/5 backdrop-blur-xl border border-gray-700/50
                shadow-[0_0_25px_-10px_rgba(156,163,175,0.3)]
                hover:shadow-[0_0_35px_-10px_rgba(156,163,175,0.5)]
                hover:border-gray-600/50 hover:-translate-y-1
                transition-all duration-300
              "
            >
              {/* Subtle gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-500/5 via-transparent to-transparent" />
              {/* Top highlight */}
              <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-gray-400/15 to-transparent" />

              <div className="relative">
                <h3 className="text-2xl font-bold text-gray-300 mb-6">
                  {t("isNotTitle")}
                </h3>
                <ul className="space-y-4">
                  {isNotItems.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <X className="w-4 h-4 text-red-400" />
                      </div>
                      <span className="text-gray-400">{t(`isNot.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>

          {/* What It Is */}
          <ScrollReveal delay={0.15}>
            <div
              className="
                relative overflow-hidden rounded-2xl p-8 h-full
                bg-gradient-to-br from-pink-500/10 to-purple-500/10 backdrop-blur-xl
                border border-pink-500/30
                shadow-[0_0_30px_-10px_rgba(236,72,153,0.4)]
                hover:shadow-[0_0_40px_-10px_rgba(236,72,153,0.6)]
                hover:border-pink-500/50 hover:-translate-y-1
                transition-all duration-300
              "
            >
              {/* Subtle gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/8 via-transparent to-transparent" />
              {/* Top highlight */}
              <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-pink-400/20 to-transparent" />

              <div className="relative">
                <h3 className="text-2xl font-bold mb-6">
                  <GradientText>{t("isTitle")}</GradientText>
                </h3>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <p className="text-gray-200 text-lg leading-relaxed">
                    {t("isStatement")}
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
